import React, { useState } from 'react';
import { Layout, Menu, Avatar, Dropdown, Button, theme, Space, Drawer, Grid } from 'antd';
import {
  BookOutlined,
  TagsOutlined,
  UserOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MenuOutlined,
  TeamOutlined,
  FileTextOutlined,
  PictureOutlined,
  RocketOutlined,
  CustomerServiceOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks';
import { ROUTES } from '../../constants';

const { Header, Sider, Content } = Layout;
const { useBreakpoint } = Grid;

interface AppLayoutProps {
  children: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const screens = useBreakpoint();
  const isMobile = !screens.md; // md is 768px

  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const {
    token: { colorBgContainer, colorBorder, colorText },
  } = theme.useToken();

  // Close mobile drawer when route changes or screen resizes to desktop
  React.useEffect(() => {
    setMobileDrawerOpen(false);
  }, [location.pathname, isMobile]);

  const menuItems = [
    {
      key: ROUTES.SOURCE_STORIES,
      icon: <FileTextOutlined />,
      label: 'Content Management',
    },
    {
      key: ROUTES.AUTO_TASKS,
      icon: <RocketOutlined />,
      label: 'Auto Pipeline',
    },
    {
      key: ROUTES.PROMPT_TEMPLATES,
      icon: <FileTextOutlined />,
      label: 'Prompt Templates',
    },
    {
      key: ROUTES.IMAGE_STYLES,
      icon: <PictureOutlined />,
      label: 'Style Library',
    },
    {
      key: ROUTES.AUDIO_LIBRARY,
      icon: <CustomerServiceOutlined />,
      label: 'Audio Library',
    },
    {
      key: ROUTES.STORIES,
      icon: <BookOutlined />,
      label: 'Stories',
    },
    {
      key: ROUTES.TAGS,
      icon: <TagsOutlined />,
      label: 'Tags',
    },
    {
      key: ROUTES.USERS,
      icon: <TeamOutlined />,
      label: 'Users',
    },
  ];

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: logout,
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  const LogoComponent = ({ isDrawer = false }: { isDrawer?: boolean }) => (
    <div style={{
      height: 64,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderBottom: `1px solid ${colorBorder}`,
      padding: '0 16px',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    }}
    onClick={() => {
      navigate('/');
      if (isMobile) setMobileDrawerOpen(false);
    }}
    >
      {(collapsed && !isDrawer) ? (
        <div style={{
          width: 32,
          height: 32,
          background: '#FF4444',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontWeight: 'bold',
          fontSize: '18px',
          flexShrink: 0,
        }}>
          T
        </div>
      ) : (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <div style={{
            width: 32,
            height: 32,
            background: '#FF4444',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '18px',
            flexShrink: 0,
          }}>
            T
          </div>
          <span style={{
            fontWeight: '700',
            fontSize: '16px',
            color: colorText,
            whiteSpace: 'nowrap',
          }}>
            TALEBOX
          </span>
        </div>
      )}
    </div>
  );

  const SideMenu = () => (
    <Menu
      mode="inline"
      selectedKeys={[location.pathname]}
      items={menuItems}
      onClick={handleMenuClick}
      style={{
        border: 'none',
        padding: '8px 0',
        height: 'calc(100vh - 64px)',
        overflowY: 'auto',
      }}
    />
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {!isMobile ? (
        <Sider 
          trigger={null} 
          collapsible 
          collapsed={collapsed}
          width={240}
          style={{
            background: '#fff',
            borderRight: `1px solid ${colorBorder}`,
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            height: '100vh',
            zIndex: 100,
          }}
        >
          <LogoComponent />
          <SideMenu />
        </Sider>
      ) : (
        <Drawer
          placement="left"
          onClose={() => setMobileDrawerOpen(false)}
          open={mobileDrawerOpen}
          styles={{ body: { padding: 0 } }}
          width={240}
          closable={false}
        >
          <LogoComponent isDrawer />
          <SideMenu />
        </Drawer>
      )}
      <Layout
        style={{
          marginLeft: isMobile ? 0 : (collapsed ? 80 : 240),
          transition: 'margin-left 0.2s ease',
          height: '100vh',
        }}
      >
        <Header style={{
          padding: isMobile ? '0 16px' : '0 24px',
          background: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${colorBorder}`,
          height: 64,
          flexShrink: 0,
          zIndex: 90,
        }}>
          <Button
            type="text"
            icon={isMobile ? <MenuOutlined /> : (collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />)}
            onClick={() => isMobile ? setMobileDrawerOpen(true) : setCollapsed(!collapsed)}
            style={{
              fontSize: '16px',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          />

          <Space size={isMobile ? 12 : 24} style={{ marginLeft: 'auto' }}>
            <div style={{
              display: isMobile ? 'none' : 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              color: colorText,
            }}>
              <span>{user?.fullName || user?.username || user?.userName}</span>
            </div>
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Avatar
                icon={<UserOutlined />}
                style={{
                  cursor: 'pointer',
                  background: '#FF4444',
                  fontSize: '16px',
                }}
              />
            </Dropdown>
          </Space>
        </Header>
        <Content
          style={{
            padding: isMobile ? 12 : 24,
            margin: isMobile ? 0 : '24px',
            height: isMobile ? 'calc(100vh - 64px)' : 'calc(100vh - 64px - 48px)',
            overflow: 'auto',
            background: colorBgContainer,
            borderRadius: isMobile ? 0 : '8px',
          }}
        >
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};

export default AppLayout;


